import { ArrowBack } from '@mui/icons-material';
import {
  IconButton,
  Chip,
  Avatar,
  Skeleton,
  Box,
  Stack,
  Tabs,
  Tab,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadButton from '../Layout/UploadButton';
import PhoneInput from '../inputs/PhoneInput';
import React, { useState, useEffect, useCallback, useReducer, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import toast from "react-hot-toast";
import axios from 'axios';
import CareerProfile from './career/CareerProfile';
import './bussinessCenter.css';
import { get, post } from '../../utility/fetchWeb';
import notification from '../../utility/notification';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Reducer for form state (prevents unnecessary re-renders)
const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_MULTIPLE':
      return { ...state, ...action.payload };
    case 'ADD_CLINIC':
      return {
        ...state,
        associatedClinics: state.associatedClinics.includes(action.id)
          ? state.associatedClinics
          : [...state.associatedClinics, action.id],
      };
    case 'REMOVE_CLINIC':
      return {
        ...state,
        associatedClinics: state.associatedClinics.filter(id => id !== action.id),
      };
    case 'ADD_LANGUAGE':
      const exists = state.spokenLanguages.some(l => l.spokenLanguageId === action.id);
      return exists
        ? state
        : { ...state, spokenLanguages: [...state.spokenLanguages, { spokenLanguageId: action.id }] };
    case 'REMOVE_LANGUAGE':
      return {
        ...state,
        spokenLanguages: state.spokenLanguages.filter(l => l.spokenLanguageId !== action.id),
      };
    default:
      return state;
  }
};

const EditEmployeeProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(id ? parseInt(id) : 0);

  // Hospital/Lab Profile States
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [services, setServices] = useState([]);
  const [aboutLabData, setAboutLabData] = useState({});

  // Form states for each section
  const [doctorForm, setDoctorForm] = useState({ fullName: '', clinicId: '', profileImage: '', specialty: '' });
  const [equipmentForm, setEquipmentForm] = useState({ name: '', description: '', imageUrl: '', clinicId: '' });
  const [servicesForm, setServicesForm] = useState({ name: '', description: '' });
  const [aboutLabForm, setAboutLabForm] = useState({
    healthCareProviderId: '',
    labDetails: '',
    thingToKnow: '',
    workDays: '',
    workHours: '',
    healthCareProviderPaymentOptions: [],
  });

  // Geography
  const [nationalities, setNationalities] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedStateId, setSelectedStateId] = useState(null);

  // Lists
  const [clinics, setClinics] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [specialties, setSpecialties] = useState([]);

  // Search terms
  const [searchClinicTerm, setSearchClinicTerm] = useState('');
  const [searchLanguageTerm, setSearchLanguageTerm] = useState('');
  const [showClinicDropdown, setShowClinicDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Work schedules (separate state)
  const [workSchedules, setWorkSchedules] = useState([]);

  // Logo
  const [logoPreview, setLogoPreview] = useState(null);

  // Employee raw data
  const [employeeData, setEmployeeData] = useState(null);

  // Form state via reducer
  const [formData, dispatch] = useReducer(formReducer, {
    govtIssuedIDType: 1,
    govtIssuedID: '',
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    gender: '',
    dateOfBirth: '',
    placeOfBirth: '',
    stateId: 0,
    lga: '',
    city: '',
    nationalityId: 0,
    officeAddress: '',
    spokenLanguages: [],
    companyLogoUrl: '',
    geoNationality: '',
    geoState: '',
    associatedClinics: [],
    profileType: 0,
    description: '',
    specialtyId: 0,
    licenseNumber: '',
  });

  // Date picker state for dateOfBirth
  const [selectedDateOfBirth, setSelectedDateOfBirth] = useState(null);

  // Optimized date/time helpers
  const convertDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    return parts.length === 3 && parts[0].length === 4
      ? `${parts[2]}-${parts[1]}-${parts[0]}`
      : dateString;
  };

  const convertDateForAPI = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    return parts.length === 3 && parts[0].length === 4
      ? `${parts[2]}-${parts[1]}-${parts[0]}`
      : dateString;
  };

  const convertTimeToString = (timeObj) => {
    if (!timeObj) return '09:00';
    if (typeof timeObj === 'string') return timeObj;
    const h = (timeObj.hours || 0).toString().padStart(2, '0');
    const m = (timeObj.minutes || 0).toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  // Memoized selected items
  const selectedClinics = useMemo(() => {
    return clinics.filter(clinic => formData.associatedClinics.includes(clinic.id));
  }, [clinics, formData.associatedClinics]);

  const selectedLanguages = useMemo(() => {
    return languages.filter(lang =>
      formData.spokenLanguages.some(l => l.spokenLanguageId === lang.id)
    );
  }, [languages, formData.spokenLanguages]);

  const filteredClinics = useMemo(() => {
    return clinics.filter(c => c.name.toLowerCase().includes(searchClinicTerm.toLowerCase()));
  }, [clinics, searchClinicTerm]);

  const filteredLanguages = useMemo(() => {
    return languages.filter(l => l.name.toLowerCase().includes(searchLanguageTerm.toLowerCase()));
  }, [languages, searchLanguageTerm]);

  // Geography fetches
  const getNationalities = useCallback(async () => {
    try {
      const res = await get('/Geography/countries');
      if (Array.isArray(res)) setNationalities(res);
    } catch (err) {
      toast.error('Failed to load nationalities');
    }
  }, []);

  const getStates = useCallback(async (countryId) => {
    try {
      const res = await get(`/Geography/countries/${countryId}/states`);
      if (Array.isArray(res)) setStates(res);
    } catch (err) {
      toast.error('Failed to load states');
    }
  }, []);

  const getCities = useCallback(async (stateId) => {
    try {
      const res = await get(`/Geography/states/${stateId}/cities`);
      if (Array.isArray(res)) setCities(res);
    } catch (err) {
      toast.error('Failed to load cities');
    }
  }, []);


  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      try {
        const userId = sessionStorage.getItem('userId');
        const clinicId = sessionStorage.getItem('clinicId');
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        await getNationalities();

        const [empRes, clinicRes, langRes, specRes] = await Promise.all([
          fetch(`${backendUrl}/employee/employee/${userId}`),
          fetch(`${backendUrl}/healthcareprovider/list/1/100`),
          fetch(`${backendUrl}/employee/spoken-language/list`),
          fetch(`${backendUrl}/rolespecialist/list`),
        ]);

        const [empData, clinicData, langData, specData] = await Promise.all([
          empRes.json(),
          clinicRes.json(),
          langRes.json(),
          specRes.json(),
        ]);

        // Fetch equipment, doctors, and services data if clinicId exists
        if (clinicId) {
          try {
            const [equipmentRes, doctorsRes, servicesRes] = await Promise.all([
              fetch(`${backendUrl}/equipment/${clinicId}`),
              fetch(`${backendUrl}/doctors/${clinicId}`),
              fetch(`${backendUrl}/services/${clinicId}`),
            ]);

            const [equipmentData, doctorsData, servicesData] = await Promise.all([
              equipmentRes.json(),
              doctorsRes.json(),
              servicesRes.json(),
            ]);

            if (Array.isArray(equipmentData)) {
              setEquipment(equipmentData);
            }

            if (Array.isArray(doctorsData)) {
              setDoctors(doctorsData);
            }

            if (Array.isArray(servicesData)) {
              setServices(servicesData);
            }
          } catch (err) {
            console.error('Failed to load equipment, doctors, or services:', err);
          }
        }

        if (empData.isSuccess) {
          const d = empData.data;
          setEmployeeData(d);
          setLogoPreview(d.companyLogoUrl || d.profilePicture);

          const govtType = d.bvn ? 2 : d.driverLicense ? 3 : 1;
          const govtId = d.bvn || d.driverLicense || d.nin || '';

          const uniqueLangs = [];
          const seen = new Set();
          d.spokenLanguages?.forEach(item => {
            if (item.spokenLanguage && !seen.has(item.spokenLanguage.id)) {
              seen.add(item.spokenLanguage.id);
              uniqueLangs.push({ spokenLanguageId: item.spokenLanguage.id });
            }
          });

          const schedules = (d.workSchedule || []).map(s => ({
            shiftDate: s.shiftDate ? s.shiftDate.split('T')[0] : new Date().toISOString().split('T')[0],
            startTime: convertTimeToString(s.startTime),
            endTime: convertTimeToString(s.endTime),
            notes: s.notes || '',
          }));
          setWorkSchedules(schedules);

          const dobValue = convertDateForDisplay(d.dateOfBirth) || '';
          dispatch({
            type: 'SET_MULTIPLE',
            payload: {
              govtIssuedIDType: govtType,
              govtIssuedID: govtId,
              title: d.title || '',
              firstName: d.firstName || '',
              middleName: d.middleName || '',
              lastName: d.lastName || '',
              phoneNumber: d.phoneNumber || '',
              email: d.email || '',
              gender: d.gender || '',
              dateOfBirth: dobValue,
              placeOfBirth: d.placeOfBirth || '',
              stateId: d.state?.id || 1,
              lga: d.lga || '',
              city: d.city || '',
              nationalityId: d.nationality?.id || 17,
              geoNationality: d.geoNationality || '',
              geoState: d.geoState || '',
              officeAddress: d.officeAddress || '',
              spokenLanguages: uniqueLangs,
              companyLogoUrl: d.companyLogoUrl || '',
              associatedClinics: d.associatedClinics || [],
              description: d.description || '',
              specialtyId: d.specialtyId || 0,
            },
          });

          // Set date picker state
          if (dobValue) {
            try {
              const [day, month, year] = dobValue.split('-');
              if (day && month && year) {
                setSelectedDateOfBirth(new Date(year, month - 1, day));
              }
            } catch (e) {
              console.error('Invalid date format:', dobValue);
            }
          }

          if (d.nationality?.id) {
            setSelectedCountryId(d.nationality.id);
            await getStates(d.nationality.id);
            if (d.state?.id) {
              setSelectedStateId(d.state.id);
              await getCities(d.state.id);
            }
          }
        }

        if (clinicData.resultList) setClinics(clinicData.resultList);
        if (langData.isSuccess) setLanguages(langData.data);
        if (specData.isSuccess) setSpecialties(specData.data);

        setLoading(false);
      } catch (err) {
        toast.error('Failed to load profile');
        setLoading(false);
      }
    };

    loadData();
  }, [getNationalities, getStates, getCities]);

  





  // Fetch About Lab data on component mount
  useEffect(() => {
    const healthCareProviderId = sessionStorage.getItem('healthCareProviderId');
    const fetchAboutLabData = async () => {
      if (healthCareProviderId) {
        try {
          const token = sessionStorage.getItem('token');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/aboutlab/healthcareprovider/id/${healthCareProviderId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (response?.status === 200 && response.data) {
            setAboutLabData(response.data);
            setAboutLabForm({
              healthCareProviderId: response.data.healthCareProviderId,
              labDetails: response.data.labDetails || '',
              thingToKnow: response.data.thingToKnow || '',
              workDays: response.data.workDays || '',
              workHours: response.data.workHours || '',
              healthCareProviderPaymentOptions: response.data.healthCareProviderPaymentOptions || [],
            });
          }
        } catch (error) {
          console.error('Failed to fetch about lab information:', error);
        }
      }
    };
    fetchAboutLabData();
  }, []);

  // Handlers
  const handleFieldChange = useCallback((field) => (e) => {
    dispatch({ type: 'SET_FIELD', field, value: e.target.value });
  }, []);

  const handleDateOfBirthChange = (date) => {
    setSelectedDateOfBirth(date);

    if (date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const formatted = `${day}-${month}-${year}`;
      dispatch({ type: 'SET_FIELD', field: 'dateOfBirth', value: formatted });
    } else {
      dispatch({ type: 'SET_FIELD', field: 'dateOfBirth', value: '' });
    }
  };

  const handleNationalityChange = (e) => {
    const name = e.target.value;

    dispatch({ type: 'SET_FIELD', field: 'geoNationality', value: name });
    dispatch({ type: 'SET_FIELD', field: 'geoState', value: '' });
    dispatch({ type: 'SET_FIELD', field: 'city', value: '' });

    const country = nationalities.find(
      n => n.name.toLowerCase() === name.toLowerCase()
    );

    setStates([]);
    setCities([]);

    if (country) {
      getStates(country.id);
      setSelectedCountryId(country.id);
    }
  };


  const handleStateChange = (e) => {
    const name = e.target.value;

    dispatch({ type: 'SET_FIELD', field: 'geoState', value: name });
    dispatch({ type: 'SET_FIELD', field: 'city', value: '' });

    const state = states.find(
      s => s.name.toLowerCase() === name.toLowerCase()
    );

    setCities([]);

    if (state) {
      getCities(state.id);
      setSelectedStateId(state.id);
    }
  };


  const addClinic = (clinic) => {
    dispatch({ type: 'ADD_CLINIC', id: clinic.id });
    setSearchClinicTerm('');
    setShowClinicDropdown(false);
  };

  const addLanguage = (lang) => {
    dispatch({ type: 'ADD_LANGUAGE', id: lang.id });
    setSearchLanguageTerm('');
    setShowLanguageDropdown(false);
  };

  const removeClinic = (clinicId) => {
    dispatch({ type: 'REMOVE_CLINIC', id: clinicId });
  };

  const removeLanguage = (languageId) => {
    dispatch({ type: 'REMOVE_LANGUAGE', id: languageId });
  };

  const handleClinicSearch = (term) => {
    setSearchClinicTerm(term);
    setShowClinicDropdown(term.length > 0);
  };

  const handleLanguageSearch = (term) => {
    setSearchLanguageTerm(term);
    setShowLanguageDropdown(term.length > 0);
  };

  const handleWorkScheduleChange = (index, field) => (e) => {
    setWorkSchedules(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: e.target.value };
      return updated;
    });
  };

  const handleWorkScheduleDateChange = (index, date) => {
    setWorkSchedules(prev => {
      const updated = [...prev];
      if (date) {
        updated[index] = { ...updated[index], shiftDate: date.toISOString().split('T')[0] };
      } else {
        updated[index] = { ...updated[index], shiftDate: '' };
      }
      return updated;
    });
  };

  const addWorkSchedule = () => {
    setWorkSchedules(prev => [...prev, {
      shiftDate: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '17:00',
      notes: '',
    }]);
  };

  const removeWorkSchedule = (index) => {
    setWorkSchedules(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const userId = sessionStorage.getItem('userId');
      const token = sessionStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const payload = {
        ...formData,
        dateOfBirth: convertDateForAPI(formData.dateOfBirth),
        specialtyId: parseInt(formData.specialtyId) || 0,
        workSchedule: workSchedules.map(s => ({
          shiftDate: new Date(s.shiftDate + 'T00:00:00Z').toISOString(),
          startTime: { hours: parseInt(s.startTime.split(':')[0]), minutes: parseInt(s.startTime.split(':')[1]), seconds: 0 },
          endTime: { hours: parseInt(s.endTime.split(':')[0]), minutes: parseInt(s.endTime.split(':')[1]), seconds: 0 },
          notes: s.notes || '',
        })),
      };

      const res = await fetch(`${backendUrl}/employee/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text().catch(() => 'Server error'));

      const data = await res.json();
      if (data.isSuccess) {
        toast.success('Profile updated successfully!');
        navigate(-1);
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (err) {
      toast.error('Update failed: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoSuccess = (res) => {
    if (res?.doclink) {
      dispatch({ type: 'SET_FIELD', field: 'companyLogoUrl', value: res.doclink });
      setLogoPreview(res.doclink);
    }
  };

  if (loading) {
    return (
      <div className="edit-business-container">
        <div className="profile-card">
          <Skeleton variant="circular" width={80} height={80} />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="rectangular" height={300} />
        </div>
        <div className="update-card">
          <Skeleton variant="text" height={60} />
          <Stack spacing={2} mt={3}>
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={56} />
            ))}
          </Stack>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-business-container">
      {/* Left Profile Card */}
      <div className="profile-card">
        <div className="profile-content">
          <div className="profile-avatar-section">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                src={logoPreview || employeeData?.profilePicture}
                sx={{ width: 80, height: 80, mb: 2 }}
              >
                {employeeData?.firstName?.[0]}{employeeData?.lastName?.[0]}
              </Avatar>
              <UploadButton
                acceptedFiles="image/*"
                buttonText="Upload Logo"
                onUploadSuccess={handleLogoSuccess}
                onUploadError={() => toast.error('Upload failed')}
              />
            </div>
            <p className="profile-title">{employeeData?.title}</p>
            <p className="profile-name">{employeeData?.firstName} {employeeData?.lastName}</p>
          </div>

          <div className="profile-details-section">
            <div className="profile-detail-item">
              <span className="detail-label">Staff ID:</span>
              <span className="detail-value">{employeeData?.staffId}</span>
            </div>
            <div className="profile-detail-item">
              <span className="detail-label">Department:</span>
              <span className="detail-value">{employeeData?.department}</span>
            </div>
            <div className="profile-detail-item">
              <span className="detail-label">Date of Birth:</span>
              <span className="detail-value">
                {employeeData?.dateOfBirth}
              </span>
            </div>
            <div className="profile-detail-item">
              <span className="detail-label">Resumption Date:</span>
              <span className="detail-value">{employeeData?.resumptionDate}</span>
            </div>
            <div className="profile-detail-item">
              <span className="detail-label">Role:</span>
              <span className="detail-value">
                {employeeData?.userRoles?.[0]?.role?.name || 'N/A'}
              </span>
            </div>
          </div>

          <div className="account-creation-section">
            <div className="creation-date-chip">
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBack />
              </IconButton>
              <p>Back to Profile</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="update-card">
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="Edit Profile" />
            <Tab label="Certificates/Licenses" />
            <Tab label="Hospital/Lab Profile" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <form onSubmit={handleSubmit} className="form-container">
            {/* Personal Information Section */}
            <div className="form-section-divider">
              <h3>Personal Information</h3>
            </div>

            <div className="form-row">
              <label className="field-label">Government Issued ID Type</label>
              <select
                value={formData.govtIssuedIDType}
                onChange={handleFieldChange('govtIssuedIDType')}
                className="form-select"
              >
                <option value={1}>NIN</option>
                <option value={2}>BVN</option>
                <option value={3}>Driver's License</option>
              </select>
            </div>

            <div className="form-row">
              <label className="field-label">ID Number</label>
              <input
                type="text"
                value={formData.govtIssuedID}
                onChange={handleFieldChange('govtIssuedID')}
                className="form-input"
              />
            </div>

            <div className="form-row-double">
              <div className="form-field-half">
                <label className="field-label">Title</label>
                <select
                  value={formData.title}
                  onChange={handleFieldChange('title')}
                  className="form-select"
                >
                  <option value="">Select Title</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                </select>
              </div>
              <div className="form-field-half">
                <label className="field-label">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={handleFieldChange('firstName')}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row-double">
              <div className="form-field-half">
                <label className="field-label">Middle Name</label>
                <input
                  type="text"
                  value={formData.middleName}
                  onChange={handleFieldChange('middleName')}
                  className="form-input"
                />
              </div>
              <div className="form-field-half">
                <label className="field-label">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={handleFieldChange('lastName')}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row-double">
              <div className="form-field-half">
                <label className="field-label">Gender</label>
                <select
                  value={formData.gender}
                  onChange={handleFieldChange('gender')}
                  className="form-select"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-field-half">
                <label className="field-label">Date of Birth</label>
                <DatePicker
                  selected={selectedDateOfBirth}
                  onChange={handleDateOfBirthChange}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  maxDate={new Date()}
                  className="form-input"
                  calendarClassName="border border-gray-200 rounded-xl shadow-xl"
                  wrapperClassName="w-full"
                />
              </div>
            </div>

            <div className="form-row-double">
              <div className="form-field-half">
                <label className="field-label">Nationality</label>
                <select
                  value={formData.geoNationality}
                  onChange={handleNationalityChange}
                  className="form-select"
                >
                  <option value={''}>Select Nationality</option>
                  {nationalities.map((nat) => (
                    <option key={nat.id} value={nat.name}>{nat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field-half">
                <label className="field-label">Place of Birth</label>
                <input
                  type="text"
                  value={formData.placeOfBirth}
                  onChange={handleFieldChange('placeOfBirth')}
                  className="form-input"
                />
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="form-section-divider">
              <h3>Contact Information</h3>
            </div>

            <div className="form-row-double">
              <div className="form-field-half">
                <label className="field-label">Phone Number</label>
                <PhoneInput
                  value={formData.phoneNumber}
                  onChange={(value) => dispatch({ type: 'SET_FIELD', field: 'phoneNumber', value })}
                  required
                />
              </div>
              <div className="form-field-half">
                <label className="field-label">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={handleFieldChange('email')}
                  className="form-input"
                  required
                />
              </div>
            </div>

            {/* Address Information Section */}
            <div className="form-section-divider">
              <h3>Address Information</h3>
            </div>

            <div className="form-row-double">
              <div className="form-field-half">
                <label className="field-label">State</label>
                <select
                  value={formData.geoState}
                  onChange={handleStateChange}
                  className="form-select"
                  disabled={!selectedCountryId}
                >
                  <option value={''}>Select State</option>
                  {states.map(state => (
                    <option key={state.id} value={state.name}>{state.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field-half">
                <label className="field-label">LGA</label>
                <input
                  type="text"
                  value={formData.lga}
                  onChange={handleFieldChange('lga')}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row-double">
              <div className="form-field-half">
                <label className="field-label">City</label>
                <select
                  value={formData.city}
                  onChange={handleFieldChange('city')}
                  className="form-select"
                  disabled={!selectedStateId}
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.name}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field-half">
                <label className="field-label">Office Address</label>
                <input
                  type="text"
                  value={formData.officeAddress}
                  onChange={handleFieldChange('officeAddress')}
                  className="form-input"
                />
              </div>
            </div>

            {/* Associated Clinics Section */}
            <div className="form-section-divider">
              <h3>Associated Clinics</h3>
            </div>

            <div className="form-row">
              <label className="field-label">Search and Add Clinics</label>
              <div className="clinic-search-container">
                <input
                  type="text"
                  value={searchClinicTerm}
                  onChange={(e) => handleClinicSearch(e.target.value)}
                  placeholder="Search clinics..."
                  className="form-input"
                />
                {showClinicDropdown && (
                  <div className="clinic-dropdown">
                    {filteredClinics.length > 0 ? (
                      filteredClinics.map(clinic => (
                        <div
                          key={clinic.id}
                          className="clinic-dropdown-item"
                          onClick={() => addClinic(clinic)}
                        >
                          {clinic.name} - {clinic.healthCareProviderCategory?.name || 'Unknown'}
                        </div>
                      ))
                    ) : (
                      <div className="clinic-dropdown-item no-results">
                        No clinics found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="selected-clinics-container">
              {selectedClinics.map(clinic => (
                <Chip
                  key={clinic.id}
                  label={`${clinic.name}`}
                  onDelete={() => removeClinic(clinic.id)}
                  sx={{ m: 0.5 }}
                />
              ))}
            </div>

            {/* Spoken Languages Section */}
            <div className="form-section-divider">
              <h3>Spoken Languages</h3>
            </div>

            <div className="form-row">
              <label className="field-label">Add Languages</label>
              <div className="language-search-container">
                <input
                  type="text"
                  value={searchLanguageTerm}
                  onChange={(e) => handleLanguageSearch(e.target.value)}
                  placeholder="Search languages..."
                  className="form-input"
                />
                {showLanguageDropdown && (
                  <div className="language-dropdown">
                    {filteredLanguages.length > 0 ? (
                      filteredLanguages.map(language => (
                        <div
                          key={language.id}
                          className="language-dropdown-item"
                          onClick={() => addLanguage(language)}
                        >
                          {language.name}
                        </div>
                      ))
                    ) : (
                      <div className="language-dropdown-item no-results">
                        No languages found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="selected-languages-container">
              {selectedLanguages.map(language => (
                <Chip
                  key={language.id}
                  label={language.name}
                  onDelete={() => removeLanguage(language.id)}
                  sx={{ m: 0.5 }}
                />
              ))}
            </div>

            {/* Work Schedule Section */}
            <div className="form-section-divider">
              <h3>Work Schedule</h3>
            </div>

            {workSchedules.map((schedule, index) => (
              <div key={index} className="work-schedule-item">
                <div className="form-row-double">
                  <div className="form-field-half">
                    <label className="field-label">Shift Date</label>
                    <DatePicker
                      selected={schedule.shiftDate ? new Date(schedule.shiftDate) : null}
                      onChange={(date) => handleWorkScheduleDateChange(index, date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="DD/MM/YYYY"
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      className="form-input"
                      calendarClassName="border border-gray-200 rounded-xl shadow-xl"
                      wrapperClassName="w-full"
                    />
                  </div>
                  <div className="form-field-half">
                    <label className="field-label">Start Time</label>
                    <input
                      type="time"
                      value={schedule.startTime}
                      onChange={handleWorkScheduleChange(index, 'startTime')}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row-double">
                  <div className="form-field-half">
                    <label className="field-label">End Time</label>
                    <input
                      type="time"
                      value={schedule.endTime}
                      onChange={handleWorkScheduleChange(index, 'endTime')}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field-half">
                    <label className="field-label">Notes</label>
                    <input
                      type="text"
                      value={schedule.notes}
                      onChange={handleWorkScheduleChange(index, 'notes')}
                      className="form-input"
                      placeholder="Optional notes"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="remove-schedule-btn"
                  onClick={() => removeWorkSchedule(index)}
                >
                  Remove Schedule
                </button>
              </div>
            ))}

            <button
              type="button"
              className="add-schedule-btn"
              onClick={addWorkSchedule}
            >
              Add Work Schedule
            </button>

            {/* Additional Information Section */}
            <div className="form-section-divider">
              <h3>Additional Information</h3>
            </div>

            <div className="form-row">
              <label className="field-label">Description</label>
              <textarea
                rows={4}
                placeholder="Tell us about yourself..."
                value={formData.description}
                onChange={handleFieldChange('description')}
                className="form-textarea"
              />
            </div>

            <div className="form-row">
              <label className="field-label">Specialty</label>
              <select
                value={formData.specialtyId}
                onChange={handleFieldChange('specialtyId')}
                className="form-select"
              >
                <option value={0}>Select Specialty</option>
                {specialties.map(specialty => (
                  <option key={specialty.id} value={specialty.id}>
                    {specialty.specialistName} ({specialty.role.name})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label className="field-label">License Number</label>
              <input
                type="text"
                value={formData.licenseNumber}
                onChange={handleFieldChange('licenseNumber')}
                className="form-input"
                placeholder="Enter your license number"
              />
            </div>

            {/* Update Button */}
            <div className="update-button-section">
              <button
                type="submit"
                className="update-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={20} /> : 'Update Profile'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 1 && (
          <Box sx={{ mt: 2 }}>
            <CareerProfile />
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ mt: 2 }}>
            <HospitalLabProfile
              expandedAccordion={expandedAccordion}
              setExpandedAccordion={setExpandedAccordion}
              doctors={doctors}
              setDoctors={setDoctors}
              equipment={equipment}
              setEquipment={setEquipment}
              services={services}
              setServices={setServices}
              aboutLabData={aboutLabData}
              setAboutLabData={setAboutLabData}
              doctorForm={doctorForm}
              setDoctorForm={setDoctorForm}
              equipmentForm={equipmentForm}
              setEquipmentForm={setEquipmentForm}
              servicesForm={servicesForm}
              setServicesForm={setServicesForm}
              aboutLabForm={aboutLabForm}
              setAboutLabForm={setAboutLabForm}
              specialties={specialties}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

// Hospital/Lab Profile Component
const HospitalLabProfile = ({
  expandedAccordion,
  setExpandedAccordion,
  doctors,
  setDoctors,
  equipment,
  setEquipment,
  services,
  setServices,
  aboutLabData,
  setAboutLabData,
  doctorForm,
  setDoctorForm,
  equipmentForm,
  setEquipmentForm,
  servicesForm,
  setServicesForm,
  aboutLabForm,
  setAboutLabForm,
  specialties,
}) => {
  const healthCareProviderId = sessionStorage.getItem('healthCareProviderId');
  const clinicId = sessionStorage.getItem('clinicId');
  const doctorPhotoRef = React.useRef(null);
  const equipmentImageRef = React.useRef(null);
  const [deleteServiceModal, setDeleteServiceModal] = React.useState({ open: false, serviceId: null });
  const [isCustomDoctorSpecialty, setIsCustomDoctorSpecialty] = React.useState(false);
  const [loadingStates, setLoadingStates] = React.useState({
    addingDoctor: false,
    addingEquipment: false,
    addingService: false,
    savingAboutLab: false,
    deletingService: false,
    updatingDoctor: false,
    deletingDoctor: false,
    updatingEquipment: false,
    deletingEquipment: false,
    updatingService: false,
    updatingAboutLab: false,
    deletingAboutLab: false,
  });

  const handleAccordionChange = (panel) => {
    setExpandedAccordion(expandedAccordion === panel ? null : panel);
  };

  // Helper function to check if response status is successful (200-299)
  const isSuccessStatus = (status) => {
    return status && status >= 200 && status < 300;
  };

  // Fetch functions to refresh data
  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/doctors/${clinicId}`);
      if (response?.status === 200 && Array.isArray(response.data)) {
        setDoctors(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh doctors list:', error);
    }
  };

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/equipment/${clinicId}`);
      if (response?.status === 200 && Array.isArray(response.data)) {
        setEquipment(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh equipment list:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/services/${clinicId}`);
      if (response?.status === 200 && Array.isArray(response.data)) {
        setServices(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh services list:', error);
    }
  };

  const fetchAboutLab = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/aboutlab/healthcareprovider/id/${healthCareProviderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.status === 200 && response.data) {
        setAboutLabData(response.data);
        setAboutLabForm({
          healthCareProviderId: response.data.healthCareProviderId,
          labDetails: response.data.labDetails || '',
          thingToKnow: response.data.thingToKnow || '',
          workDays: response.data.workDays || '',
          workHours: response.data.workHours || '',
          healthCareProviderPaymentOptions: response.data.healthCareProviderPaymentOptions || [],
        });
      }
    } catch (error) {
      console.error('Failed to refresh about lab data:', error);
    }
  };

  // Doctors handlers
  const handleAddDoctor = async () => {
    if (!doctorForm.fullName || !doctorForm.specialty) {
      notification({ message: 'Please fill in all required fields', type: 'error' });
      return;
    }
    const isUpdate = doctorForm.id;
    setLoadingStates(prev => ({ ...prev, addingDoctor: true }));
    try {
      const payload = {
        fullName: doctorForm.fullName,
        clinicId: parseInt(clinicId),
        profileImage: doctorForm.profileImage,
        specialty: doctorForm.specialty,
      };

      let response;
      if (isUpdate) {
        response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/doctors/${doctorForm.id}`, payload);
      } else {
        response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/doctors`, payload);
      }

      if (isSuccessStatus(response?.status)) {
        if (isUpdate) {
          notification({ message: 'Doctor updated successfully', type: 'success' });
        } else {
          notification({ message: 'Doctor added successfully', type: 'success' });
        }
        setDoctorForm({ fullName: '', clinicId: '', profileImage: '', specialty: '' });
        await fetchDoctors();
      }
    } catch (error) {
      notification({ message: isUpdate ? 'Failed to update doctor' : 'Failed to add doctor', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, addingDoctor: false }));
    }
  };

  const handleDoctorPhotoUpload = (response) => {
    if (response?.doclink) {
      setDoctorForm({ ...doctorForm, profileImage: response.doclink });
      notification({ message: 'Photo uploaded successfully', type: 'success' });
    }
  };

  // Equipment handlers
  const handleAddEquipment = async () => {
    if (!equipmentForm.name || !equipmentForm.description) {
      notification({ message: 'Please fill in all required fields', type: 'error' });
      return;
    }
    const isUpdate = equipmentForm.id;
    setLoadingStates(prev => ({ ...prev, addingEquipment: true }));
    try {
      let payload;
      if (isUpdate) {
        payload = {
          id: equipmentForm.id,
          name: equipmentForm.name,
          description: equipmentForm.description,
          imageUrl: equipmentForm.imageUrl,
        };
      } else {
        payload = {
          name: equipmentForm.name,
          description: equipmentForm.description,
          imageUrl: equipmentForm.imageUrl,
          clinicId: parseInt(clinicId),
        };
      }

      let response;
      if (isUpdate) {
        response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/equipment/${equipmentForm.id}`, payload);
      } else {
        response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/equipment`, payload);
      }

      if (isSuccessStatus(response?.status)) {
        if (isUpdate) {
          notification({ message: 'Equipment updated successfully', type: 'success' });
        } else {
          notification({ message: 'Equipment added successfully', type: 'success' });
        }
        setEquipmentForm({ name: '', description: '', imageUrl: '', clinicId: '' });
        await fetchEquipment();
      }
    } catch (error) {
      notification({ message: isUpdate ? 'Failed to update equipment' : 'Failed to add equipment', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, addingEquipment: false }));
    }
  };

  const handleEquipmentImageUpload = (response) => {
    if (response?.doclink) {
      setEquipmentForm({ ...equipmentForm, imageUrl: response.doclink });
      notification({ message: 'Image uploaded successfully', type: 'success' });
    }
  };

  // Services handlers
  const handleAddService = async () => {
    if (!servicesForm.name) {
      notification({ message: 'Please fill in service name', type: 'error' });
      return;
    }
    const isUpdate = servicesForm.id;
    setLoadingStates(prev => ({ ...prev, addingService: true }));
    try {
      let payload;
      if (isUpdate) {
        payload = {
          id: servicesForm.id,
          name: servicesForm.name,
          description: servicesForm.description,
          isActive: servicesForm.isActive,
        };
      } else {
        payload = {
          clinicId: parseInt(clinicId),
          name: servicesForm.name,
          description: servicesForm.description,
        };
      }

      let response;
      if (isUpdate) {
        response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/services/${servicesForm.id}`, payload);
      } else {
        response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/services`, payload);
      }

      if (isSuccessStatus(response?.status)) {
        if (isUpdate) {
          notification({ message: 'Service updated successfully', type: 'success' });
        } else {
          notification({ message: 'Service added successfully', type: 'success' });
        }
        setServicesForm({ name: '', description: '' });
        await fetchServices();
      }
    } catch (error) {
      notification({ message: isUpdate ? 'Failed to update service' : 'Failed to add service', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, addingService: false }));
    }
  };

  const handleDeleteService = async () => {
    const serviceId = deleteServiceModal.serviceId;
    setDeleteServiceModal({ open: false, serviceId: null });
    setLoadingStates(prev => ({ ...prev, deletingService: true }));

    try {
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/services/${serviceId}`);
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'Service deleted successfully', type: 'success' });
        await fetchServices();
      }
    } catch (error) {
      notification({ message: 'Failed to delete service', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, deletingService: false }));
    }
  };

  // About Lab handlers
  const handleSaveAboutLab = async () => {
    setLoadingStates(prev => ({ ...prev, savingAboutLab: true }));
    try {
      const token = sessionStorage.getItem('token');
      const payload = {
        healthCareProviderId: parseInt(healthCareProviderId),
        labDetails: aboutLabForm.labDetails,
        thingToKnow: aboutLabForm.thingToKnow,
        workDays: aboutLabForm.workDays,
        workHours: aboutLabForm.workHours,
        healthCareProviderPaymentOptions: aboutLabForm.healthCareProviderPaymentOptions || [],
      };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/aboutLab`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'About Lab saved successfully', type: 'success' });
        await fetchAboutLab();
      }
    } catch (error) {
      notification({ message: 'Failed to save about lab information', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, savingAboutLab: false }));
    }
  };

  // Doctor Update and Delete handlers
  const handleUpdateDoctor = async (doctorId, updatedData) => {
    setLoadingStates(prev => ({ ...prev, updatingDoctor: true }));
    try {
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/doctors/${doctorId}`, updatedData);
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'Doctor updated successfully', type: 'success' });
        await fetchDoctors();
      }
    } catch (error) {
      notification({ message: 'Failed to update doctor', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, updatingDoctor: false }));
    }
  };

  const handleDeleteDoctor = async (doctorId) => {
    setLoadingStates(prev => ({ ...prev, deletingDoctor: true }));
    try {
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/doctors/${doctorId}`);
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'Doctor deleted successfully', type: 'success' });
        await fetchDoctors();
      }
    } catch (error) {
      notification({ message: 'Failed to delete doctor', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, deletingDoctor: false }));
    }
  };

  // Equipment Update and Delete handlers
  const handleUpdateEquipment = async (equipmentId, updatedData) => {
    setLoadingStates(prev => ({ ...prev, updatingEquipment: true }));
    try {
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/equipment/${equipmentId}`, updatedData);
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'Equipment updated successfully', type: 'success' });
        await fetchEquipment();
      }
    } catch (error) {
      notification({ message: 'Failed to update equipment', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, updatingEquipment: false }));
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    setLoadingStates(prev => ({ ...prev, deletingEquipment: true }));
    try {
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/equipment/${equipmentId}`);
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'Equipment deleted successfully', type: 'success' });
        await fetchEquipment();
      }
    } catch (error) {
      notification({ message: 'Failed to delete equipment', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, deletingEquipment: false }));
    }
  };

  // Services Update handler (Delete already exists)
  const handleUpdateService = async (serviceId, updatedData) => {
    setLoadingStates(prev => ({ ...prev, updatingService: true }));
    try {
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/services/${serviceId}`, updatedData);
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'Service updated successfully', type: 'success' });
        await fetchServices();
      }
    } catch (error) {
      notification({ message: 'Failed to update service', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, updatingService: false }));
    }
  };

  // About Lab handlers for update and delete
  const handleUpdateAboutLab = async (aboutLabId, updatedData) => {
    setLoadingStates(prev => ({ ...prev, updatingAboutLab: true }));
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/aboutLab/${aboutLabId}`, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'About Lab updated successfully', type: 'success' });
        await fetchAboutLab();
      }
    } catch (error) {
      notification({ message: 'Failed to update about lab information', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, updatingAboutLab: false }));
    }
  };

  const handleDeleteAboutLab = async (aboutLabId) => {
    setLoadingStates(prev => ({ ...prev, deletingAboutLab: true }));
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/aboutLab/${aboutLabId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (isSuccessStatus(response?.status)) {
        notification({ message: 'About Lab deleted successfully', type: 'success' });
        await fetchAboutLab();
      }
    } catch (error) {
      notification({ message: 'Failed to delete about lab information', type: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, deletingAboutLab: false }));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, padding: 2 }}>
      {/* Doctors Accordion */}
      <Box
        sx={{
          border: '1px solid #eee',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Box
          onClick={() => handleAccordionChange('doctors')}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            backgroundColor: expandedAccordion === 'doctors' ? '#e9ffe9' : '#f9f9f9',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: expandedAccordion === 'doctors' ? 600 : 500,
            color: expandedAccordion === 'doctors' ? '#28a745' : '#333',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: expandedAccordion === 'doctors' ? '#e9ffe9' : '#f0f0f0',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10C8.65685 10 10 8.65685 10 7C10 5.34315 8.65685 4 7 4C5.34315 4 4 5.34315 4 7C4 8.65685 5.34315 10 7 10ZM17 10C18.6569 10 20 8.65685 20 7C20 5.34315 18.6569 4 17 4C15.3431 4 14 5.34315 14 7C14 8.65685 15.3431 10 17 10ZM7 14C4.23858 14 1 15.3954 1 18V20H13V18C13 15.3954 9.76142 14 7 14ZM17 14C16.71 14 16.38 14.02 16.03 14.05C17.19 14.89 18 15.95 18 17.5V20H23V18C23 15.39 20.77 14 17 14Z" fill="currentColor" />
            </svg>
            <span>Care Team</span>
          </Box>
          <Box
            sx={{
              transform: expandedAccordion === 'doctors' ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              color: expandedAccordion === 'doctors' ? '#28a745' : '#888',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
            </svg>
          </Box>
        </Box>
        {expandedAccordion === 'doctors' && (
          <Box sx={{ padding: '20px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Add Doctor Form */}
            <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
              <Box sx={{ fontWeight: 600, mb: 2 }}>Add To Care Team</Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label="Full Name *"
                  value={doctorForm.fullName}
                  onChange={(e) => setDoctorForm({ ...doctorForm, fullName: e.target.value })}
                  fullWidth
                  size="small"
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {!isCustomDoctorSpecialty ? (
                    <>
                      <FormControl fullWidth size="small">
                        <TextField
                          select
                          label="Specialty *"
                          value={doctorForm.specialty}
                          onChange={(e) => setDoctorForm({ ...doctorForm, specialty: e.target.value })}
                          fullWidth
                        >
                          <MenuItem value="">Select Specialty</MenuItem>
                          {specialties.map(specialty => (
                            <MenuItem key={specialty.id} value={specialty.specialistName}>
                              {specialty.specialistName} ({specialty.role.name})
                            </MenuItem>
                          ))}
                        </TextField>
                      </FormControl>
                      <Box
                        onClick={() => {
                          setIsCustomDoctorSpecialty(true);
                          setDoctorForm({ ...doctorForm, specialty: '' });
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          backgroundColor: '#f2fff0ff',
                          border: '1px solid #d3ffd0ff',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#e4fde3ff',
                            borderColor: '#90f997ff',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 4px rgba(25, 118, 210, 0.1)'
                          }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="#3ed219ff" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#3ed219ff',
                            fontWeight: 500,
                            fontSize: '0.813rem'
                          }}
                        >
                          Specialty not in list? Add custom specialty
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <TextField
                        label="Custom Specialty *"
                        value={doctorForm.specialty}
                        onChange={(e) => setDoctorForm({ ...doctorForm, specialty: e.target.value })}
                        placeholder="Enter custom specialty"
                        fullWidth
                        size="small"
                      />
                      <Box
                        onClick={() => {
                          setIsCustomDoctorSpecialty(false);
                          setDoctorForm({ ...doctorForm, specialty: '' });
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #e0e0e0',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#eeeeee',
                            borderColor: '#bdbdbd',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19 12H5M12 5L5 12L12 19" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#666',
                            fontWeight: 500,
                            fontSize: '0.813rem'
                          }}
                        >
                          Back to specialty list
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  {doctorForm.profileImage && (
                    <Box
                      component="img"
                      src={doctorForm.profileImage}
                      alt="Care Team preview"
                      sx={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  )}
                  <UploadButton
                    acceptedFiles="image/*"
                    buttonText="Upload Photo"
                    patientRef={doctorPhotoRef}
                    onUploadSuccess={handleDoctorPhotoUpload}
                    onUploadError={() => notification({ message: 'Photo upload failed', type: 'error' })}
                  />
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={handleAddDoctor}
                disabled={loadingStates.addingDoctor}
                sx={{ mt: 2 }}
              >
                {loadingStates.addingDoctor ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  doctorForm.id ? 'Update Doctor' : 'Add To Care Team'
                )}
              </Button>
            </Box>

            {/* Doctors List */}
            {doctors.length > 0 && (
              <Box>
                <Box sx={{ fontWeight: 600, mb: 2, paddingBottom: 1, borderBottom: '1px dashed #eee' }}>Doctors Added</Box>
                {doctors.map((doctor, idx) => (
                  <Box key={idx} sx={{ p: 1.5, mb: 1, border: '1px solid #f0f0f0', borderRadius: 1, bgcolor: '#f9f9f9', display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      {doctor.profileImage && (
                        <Box
                          component="img"
                          src={doctor.profileImage}
                          alt={doctor.fullName}
                          sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                        />
                      )}
                      <Box>
                        <Box sx={{ fontWeight: 500 }}>{doctor.fullName}</Box>
                        <Box sx={{ fontSize: '0.9rem', color: '#666' }}>Specialty: {doctor.specialty}</Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setDoctorForm(doctor);
                        }}
                        sx={{ color: '#28a745', borderColor: '#28a745' }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteDoctor(doctor.id)}
                        disabled={loadingStates.deletingDoctor}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Equipment Accordion */}
      <Box
        sx={{
          border: '1px solid #eee',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Box
          onClick={() => handleAccordionChange('equipment')}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            backgroundColor: expandedAccordion === 'equipment' ? '#e9ffe9' : '#f9f9f9',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: expandedAccordion === 'equipment' ? 600 : 500,
            color: expandedAccordion === 'equipment' ? '#28a745' : '#333',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: expandedAccordion === 'equipment' ? '#e9ffe9' : '#f0f0f0',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.7 19L13.6 9.9C13.99 9.33 14.25 8.58 14.25 7.75C14.25 5.45 12.55 3.75 10.25 3.75C7.95 3.75 6.25 5.45 6.25 7.75C6.25 10.05 7.95 11.75 10.25 11.75C11.08 11.75 11.83 11.49 12.4 11.1L21.5 20.25C21.89 20.64 22.5 20.64 22.89 20.25L22.7 19ZM11.5 9.5C10.12 9.5 9 8.38 9 7C9 5.62 10.12 4.5 11.5 4.5C12.88 4.5 14 5.62 14 7C14 8.38 12.88 9.5 11.5 9.5Z" fill="currentColor" />
            </svg>
            <span>Equipment</span>
          </Box>
          <Box
            sx={{
              transform: expandedAccordion === 'equipment' ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              color: expandedAccordion === 'equipment' ? '#28a745' : '#888',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
            </svg>
          </Box>
        </Box>
        {expandedAccordion === 'equipment' && (
          <Box sx={{ padding: '20px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Add Equipment Form */}
            <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
              <Box sx={{ fontWeight: 600, mb: 2 }}>Add New Equipment</Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label="Equipment Name *"
                  value={equipmentForm.name}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Description *"
                  value={equipmentForm.description}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  {equipmentForm.imageUrl && (
                    <Box
                      component="img"
                      src={equipmentForm.imageUrl}
                      alt="Equipment preview"
                      sx={{ width: 60, height: 60, borderRadius: '4px', objectFit: 'cover' }}
                    />
                  )}
                  <UploadButton
                    acceptedFiles="image/*"
                    buttonText="Upload Image"
                    patientRef={equipmentImageRef}
                    onUploadSuccess={handleEquipmentImageUpload}
                    onUploadError={() => notification({ message: 'Image upload failed', type: 'error' })}
                  />
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={handleAddEquipment}
                disabled={loadingStates.addingEquipment}
                sx={{ mt: 2 }}
              >
                {loadingStates.addingEquipment ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  equipmentForm.id ? 'Update Equipment' : 'Add Equipment'
                )}
              </Button>
            </Box>

            {/* Equipment List */}
            {equipment.length > 0 && (
              <Box>
                <Box sx={{ fontWeight: 600, mb: 2, paddingBottom: 1, borderBottom: '1px dashed #eee' }}>Equipment Added</Box>
                {equipment.map((item, idx) => (
                  <Box key={idx} sx={{ p: 1.5, mb: 1, border: '1px solid #f0f0f0', borderRadius: 1, bgcolor: '#f9f9f9', display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      {item.imageUrl && (
                        <Box
                          component="img"
                          src={item.imageUrl}
                          alt={item.name}
                          sx={{ width: 50, height: 50, borderRadius: '4px', objectFit: 'cover' }}
                        />
                      )}
                      <Box>
                        <Box sx={{ fontWeight: 500 }}>{item.name}</Box>
                        <Box sx={{ fontSize: '0.9rem', color: '#666' }}>{item.description}</Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setEquipmentForm(item);
                        }}
                        sx={{ color: '#28a745', borderColor: '#28a745' }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteEquipment(item.id)}
                        disabled={loadingStates.deletingEquipment}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Services Accordion */}
      <Box
        sx={{
          border: '1px solid #eee',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Box
          onClick={() => handleAccordionChange('services')}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            backgroundColor: expandedAccordion === 'services' ? '#e9ffe9' : '#f9f9f9',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: expandedAccordion === 'services' ? 600 : 500,
            color: expandedAccordion === 'services' ? '#28a745' : '#333',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: expandedAccordion === 'services' ? '#e9ffe9' : '#f0f0f0',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12C17 10.34 15.66 9 14 9C12.34 9 11 10.34 11 12C11 13.66 12.34 15 14 15C15.66 15 17 13.66 17 12ZM7 8H11V13H7V8Z" fill="currentColor" />
            </svg>
            <span>Services</span>
          </Box>
          <Box
            sx={{
              transform: expandedAccordion === 'services' ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              color: expandedAccordion === 'services' ? '#28a745' : '#888',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
            </svg>
          </Box>
        </Box>
        {expandedAccordion === 'services' && (
          <Box sx={{ padding: '20px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
              <Box sx={{ fontWeight: 600, mb: 2 }}>Add New Service</Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label="Service Name *"
                  value={servicesForm.name}
                  onChange={(e) => setServicesForm({ ...servicesForm, name: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Description"
                  value={servicesForm.description}
                  onChange={(e) => setServicesForm({ ...servicesForm, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
              </Box>
              <Button
                variant="contained"
                onClick={handleAddService}
                disabled={loadingStates.addingService}
                sx={{ mt: 2 }}
              >
                {loadingStates.addingService ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  servicesForm.id ? 'Update Service' : 'Add Service'
                )}
              </Button>
            </Box>

            {services.length > 0 && (
              <Box>
                <Box sx={{ fontWeight: 600, mb: 2, paddingBottom: 1, borderBottom: '1px dashed #eee' }}>Services Added</Box>
                {services.map((service, idx) => (
                  <Box key={idx} sx={{ p: 1.5, mb: 1, border: '1px solid #f0f0f0', borderRadius: 1, bgcolor: '#f9f9f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Box sx={{ fontWeight: 500 }}>{service.name}</Box>
                      {service.description && (
                        <Box sx={{ fontSize: '0.9rem', color: '#666' }}>{service.description}</Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setServicesForm(service);
                        }}
                        sx={{ color: '#28a745', borderColor: '#28a745' }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setDeleteServiceModal({ open: true, serviceId: service.id })}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* About Lab/Hospital Accordion */}
      <Box
        sx={{
          border: '1px solid #eee',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Box
          onClick={() => handleAccordionChange('aboutLab')}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            backgroundColor: expandedAccordion === 'aboutLab' ? '#e9ffe9' : '#f9f9f9',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: expandedAccordion === 'aboutLab' ? 600 : 500,
            color: expandedAccordion === 'aboutLab' ? '#28a745' : '#333',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: expandedAccordion === 'aboutLab' ? '#e9ffe9' : '#f0f0f0',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor" />
            </svg>
            <span>About Us</span>
          </Box>
          <Box
            sx={{
              transform: expandedAccordion === 'aboutLab' ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              color: expandedAccordion === 'aboutLab' ? '#28a745' : '#888',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
            </svg>
          </Box>
        </Box>
        {expandedAccordion === 'aboutLab' && (
          <Box sx={{ padding: '20px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {aboutLabData && aboutLabData.id && (
              <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9', mb: 2 }}>
                <Box sx={{ fontWeight: 600, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Current About Lab Information</span>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ color: '#28a745', borderColor: '#28a745' }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeleteAboutLab(aboutLabData.id)}
                      disabled={loadingStates.deletingAboutLab}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Box sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#666' }}>Lab Details</Box>
                    <Box sx={{ mt: 0.5 }}>{aboutLabData.labDetails || '-'}</Box>
                  </Box>
                  <Box>
                    <Box sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#666' }}>Things to Know</Box>
                    <Box sx={{ mt: 0.5 }}>{aboutLabData.thingToKnow || '-'}</Box>
                  </Box>
                  <Box>
                    <Box sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#666' }}>Work Days</Box>
                    <Box sx={{ mt: 0.5 }}>{aboutLabData.workDays || '-'}</Box>
                  </Box>
                  <Box>
                    <Box sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#666' }}>Work Hours</Box>
                    <Box sx={{ mt: 0.5 }}>{aboutLabData.workHours || '-'}</Box>
                  </Box>
                </Box>
              </Box>
            )}
            <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
              <Box sx={{ fontWeight: 600, mb: 2 }}>Hospital/Lab Information</Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label="Lab Details"
                  value={aboutLabForm.labDetails}
                  onChange={(e) => setAboutLabForm({ ...aboutLabForm, labDetails: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                />
                <TextField
                  label="Things to Know"
                  value={aboutLabForm.thingToKnow}
                  onChange={(e) => setAboutLabForm({ ...aboutLabForm, thingToKnow: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                />
                <TextField
                  label="Work Days"
                  value={aboutLabForm.workDays}
                  onChange={(e) => setAboutLabForm({ ...aboutLabForm, workDays: e.target.value })}
                  fullWidth
                  placeholder="e.g., Monday - Friday"
                  size="small"
                />
                <TextField
                  label="Work Hours"
                  value={aboutLabForm.workHours}
                  onChange={(e) => setAboutLabForm({ ...aboutLabForm, workHours: e.target.value })}
                  fullWidth
                  placeholder="e.g., 9:00 AM - 5:00 PM"
                  size="small"
                />
              </Box>
              <Button
                variant="contained"
                onClick={aboutLabData && aboutLabData.id ? () => handleUpdateAboutLab(aboutLabData.id, { healthCareProviderId: parseInt(healthCareProviderId), labDetails: aboutLabForm.labDetails, thingToKnow: aboutLabForm.thingToKnow, workDays: aboutLabForm.workDays, workHours: aboutLabForm.workHours, healthCareProviderPaymentOptions: aboutLabForm.healthCareProviderPaymentOptions || [] }) : handleSaveAboutLab}
                disabled={loadingStates.savingAboutLab || loadingStates.updatingAboutLab}
                sx={{ mt: 2 }}
              >
                {loadingStates.savingAboutLab || loadingStates.updatingAboutLab ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  aboutLabData && aboutLabData.id ? 'Update About Lab' : 'Save About Lab'
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteServiceModal.open}
        onClose={() => setDeleteServiceModal({ open: false, serviceId: null })}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Service
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this service? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteServiceModal({ open: false, serviceId: null })}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteService}
            color="error"
            variant="contained"
            disabled={loadingStates.deletingService}
            autoFocus
          >
            {loadingStates.deletingService ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(EditEmployeeProfile);